/*
 * Single Active Tab Guard for lytsoft-backoffice
 *
 * Enforces a single-active-tab model using a localStorage lease and cross-tab signaling.
 * Shows an overlay and banner on non-leader tabs with a "Use here" takeover button.
 *
 * Implementation aligns with docs/tabGuard.md and Junie Guidelines.
 *
 * Clear-stuck instructions: In DevTools → Application → Local Storage, remove keys
 *   - lytsoft-backoffice-tab-guard:lease
 *   - lytsoft-backoffice-tab-guard:cmd
 */

const LEASE_KEY = 'lytsoft-backoffice-tab-guard:lease';
const CMD_KEY = 'lytsoft-backoffice-tab-guard:cmd';
const CHANNEL_NAME = 'lytsoft-backoffice-tab-guard';
const LEASE_TTL_MS = 5000; // ~5s
const HEARTBEAT_MS = 1500; // ~1.5s

/**
 * Generate a stable-ish random tab ID for the current session.
 * @returns {string}
 */
function genTabId() {
  try {
    const arr = new Uint32Array(3);
    (window.crypto || {}).getRandomValues && window.crypto.getRandomValues(arr);
    return `tab-${Date.now().toString(36)}-${Array.from(arr).map(n=>n.toString(36)).join('')}`;
  } catch (_) {
    return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2,10)}`;
  }
}

/**
 * Return a stable tab ID across reloads within the same browser tab using sessionStorage.
 * Falls back to a new random ID if sessionStorage is unavailable.
 * @returns {string}
 */
function getStableTabId() {
  const KEY = 'lytsoft-backoffice-tab-guard:tabId';
  try {
    let id = sessionStorage.getItem(KEY);
    if (!id) {
      id = genTabId();
      sessionStorage.setItem(KEY, id);
    }
    return id;
  } catch (_) {
    return genTabId();
  }
}

/**
 * Parse the current lease from localStorage.
 * @returns {{ownerId:string, ts:number}|null}
 */
function readLease() {
  try {
    const raw = localStorage.getItem(LEASE_KEY);
    if (!raw) return null;
    const val = JSON.parse(raw);
    if (!val || typeof val !== 'object') return null;
    return val;
  } catch (_) {
    return null;
  }
}

/**
 * Check if a lease is expired.
 * @param {{ts:number}|null} lease
 * @returns {boolean}
 */
function isLeaseExpired(lease) {
  if (!lease) return true;
  return (Date.now() - (lease.ts || 0)) > LEASE_TTL_MS;
}

/**
 * Best-effort write/renew the lease for the given ownerId.
 * @param {string} ownerId
 */
function writeLease(ownerId) {
  try {
    localStorage.setItem(LEASE_KEY, JSON.stringify({ ownerId, ts: Date.now() }));
  } catch (_) {
    // no-op (fail-open)
  }
}

/**
 * Best-effort removal of the lease.
 */
function clearLease() {
  try { localStorage.removeItem(LEASE_KEY); } catch (_) {}
}

/**
 * Send a cross-tab message using BroadcastChannel if available, otherwise via localStorage ping.
 * @param {BroadcastChannel|null} bc
 * @param {any} msg
 */
function sendCmd(bc, msg) {
  try { if (bc) bc.postMessage(msg); } catch (_) {}
  try { localStorage.setItem(CMD_KEY, JSON.stringify({ msg, ts: Date.now() })); } catch (_) {}
}

/**
 * Create the non-leader overlay and banner elements.
 * Returns functions to show/hide and wire the button callback.
 */
function createOverlayUI(onUseHere) {
  const existing = document.getElementById('lytsoft-tabguard-overlay');
  if (existing) existing.remove();

  const overlay = document.createElement('div');
  overlay.id = 'lytsoft-tabguard-overlay';
  overlay.setAttribute('role', 'dialog');
  overlay.setAttribute('aria-modal', 'true');
  overlay.style.cssText = [
    'position:fixed','inset:0','z-index:2147483000','background:rgba(0,0,0,0.35)',
    'backdrop-filter:blur(1px)','pointer-events:auto','display:none'
  ].join(';');

  const banner = document.createElement('div');
  banner.id = 'lytsoft-tabguard-banner';
  banner.setAttribute('role', 'alert');
  banner.style.cssText = [
    'position:fixed','top:0','left:0','right:0','z-index:2147483001',
    'background:#558B2F','color:white','padding:10px 12px',
    'display:flex','align-items:center','justify-content:space-between',
    'font-family:system-ui, -apple-system, Segoe UI, Roboto, Ubuntu, Cantarell, Noto Sans, Arial','font-size:14px',
    'box-shadow:0 2px 6px rgba(0,0,0,0.2)','display:none'
  ].join(';');

  const text = document.createElement('div');
  text.textContent = 'Another lytsoft-backoffice tab is active. Use here to switch.';
  text.style.cssText = 'margin-right:8px;'

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = 'Use here';
  btn.style.cssText = [
    'appearance:none','border:none','background:#ffc107','color:#000',
    'border-radius:6px','padding:6px 10px','font-weight:600','cursor:pointer'
  ].join(';');
  btn.addEventListener('click', (e) => { e.preventDefault(); onUseHere && onUseHere(); });

  banner.appendChild(text);
  banner.appendChild(btn);

  // Block all pointer/keyboard events except F5/Ctrl+R by overlaying an invisible blocker
  overlay.addEventListener('keydown', (e) => {
    const key = e.key?.toLowerCase();
    const allow = key === 'f5' || (e.ctrlKey && key === 'r');
    if (!allow) {
      e.preventDefault();
      e.stopPropagation();
    }
  }, true);
  overlay.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); }, true);

  document.body.appendChild(overlay);
  document.body.appendChild(banner);

  return {
    show() { overlay.style.display = 'block'; banner.style.display = 'flex'; },
    hide() { overlay.style.display = 'none'; banner.style.display = 'none'; },
    remove() { overlay.remove(); banner.remove(); }
  };
}

/**
 * Attempt to acquire leadership by writing the lease if it's expired or empty.
 * @param {string} tabId
 * @returns {boolean} true if acquired
 */
function tryAcquire(tabId) {
  const lease = readLease();
  if (!lease || isLeaseExpired(lease)) {
    writeLease(tabId);
    // Verify acquired
    const verify = readLease();
    return !!verify && verify.ownerId === tabId;
  }
  return lease.ownerId === tabId;
}

/**
 * Emit tab leadership changes via a global EventBus if present.
 * @param {boolean} isLeader
 * @param {string} tabId
 */
function emitEventBus(isLeader, tabId) {
  try {
    const bus = window.EventBus || window.eventBus || null;
    if (bus && typeof bus.$emit === 'function') {
      bus.$emit('tab-leadership-changed', { isLeader, tabId });
    }
  } catch (_) {}
}

/**
 * Initialize the single-active-tab guard for this tab.
 * Exposes a debug controller on window.tabGuardLeader.
 *
 * @returns {{ isLeader: () => boolean, forceTakeover: () => Promise<void>, stop: () => void }} controller
 */
export function initTabGuardLeader() {
  // Optional kill switch
  // @ts-ignore
  if (window.__TAB_GUARD_DISABLED__) {
    return attachDebugController({
      isLeader: () => true,
      forceTakeover: async () => {},
      stop: () => {}
    });
  }

  // Set up state
  const tabId = getStableTabId();
  let bc = null;
  try { bc = new BroadcastChannel(CHANNEL_NAME); } catch (_) { bc = null; }

  let heartbeatTimer = null;
  let followerTimer = null;
  let stopped = false;
  let iAmLeader = false;
  let releaseOnRequest = false; // set by takeover-request

  const ui = createOverlayUI(() => {
    // Takeover button: request leadership and ask current leader to close
    sendCmd(bc, { type: 'takeover-request', from: tabId });
    sendCmd(bc, { type: 'please-close', from: tabId });
    setTimeout(() => { safeTryAcquire(); }, 250);
  });

  function becomeLeader() {
    if (iAmLeader) return;
    iAmLeader = true;
    ui.hide();
    emitEventBus(true, tabId);
    // Notify others we now hold leadership; may trigger old leader to close itself
    sendCmd(bc, { type: 'leadership-confirmed', from: tabId });
    scheduleHeartbeat();
  }

  function becomeFollower() {
    if (!iAmLeader) return;
    iAmLeader = false;
    ui.show();
    emitEventBus(false, tabId);
    clearHeartbeat();
  }

  function scheduleHeartbeat() {
    clearHeartbeat();
    heartbeatTimer = setInterval(() => {
      if (stopped) return;
      if (releaseOnRequest) {
        // Leader gracefully releases the lease
        clearLease();
        sendCmd(bc, { type: 'lease-released', from: tabId });
        releaseOnRequest = false;
        // Drop leadership so someone else can acquire; we will attempt reacquire only if user requests
        becomeFollower();
        return;
      }
      writeLease(tabId);
    }, HEARTBEAT_MS);
  }

  function clearHeartbeat() {
    if (heartbeatTimer) {
      clearInterval(heartbeatTimer);
      heartbeatTimer = null;
    }
  }

  function scheduleFollowerCheck() {
    if (followerTimer) return;
    followerTimer = setInterval(() => {
      if (stopped || iAmLeader) return;
      const acquired = tryAcquire(tabId);
      if (acquired) {
        becomeLeader();
      } else {
        ui.show();
      }
    }, HEARTBEAT_MS);
  }

  function clearFollowerCheck() {
    if (followerTimer) {
      clearInterval(followerTimer);
      followerTimer = null;
    }
  }

  function safeTryAcquire() {
    if (stopped) return;
    const acquired = tryAcquire(tabId);
    if (acquired) becomeLeader();
  }

  /**
   * Best-effort attempt to close this tab when it is no longer needed.
   * Tries programmatic close; if blocked by the browser, navigates to a neutral
   * context and shows an interstitial asking the user to close the tab.
   */
  function attemptCloseSelf() {
    // First attempt: direct close (works if script-opened or in some PWAs/WebViews)
    try { window.close(); } catch (_) {}

    // Second attempt: replace location then close
    setTimeout(() => {
      try { location.replace('about:blank'); } catch (_) {}
      try { window.close(); } catch (_) {}

      // Final fallback: render a blocking interstitial and stop activity
      setTimeout(() => {
        try { stop(); } catch (_) {}
        if (!document.body) return;
        try { document.body.innerHTML = ''; } catch (_) {}

        const wrap = document.createElement('div');
        wrap.setAttribute('role', 'dialog');
        wrap.setAttribute('aria-live', 'assertive');
        wrap.style.cssText = [
          'position:fixed','inset:0','z-index:2147483647','background:#0b1021',
          'color:#fff','display:flex','flex-direction:column','align-items:center',
          'justify-content:center','padding:24px','text-align:center'
        ].join(';');

        const h1 = document.createElement('h1');
        h1.textContent = 'This tab is now inactive';
        h1.style.marginBottom = '12px';

        const p = document.createElement('p');
        p.textContent = 'To prevent conflicts, please close this tab now.';
        p.style.marginBottom = '16px';

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.textContent = 'Close this tab';
        btn.style.cssText = [
          'background:#1877f2','color:#fff','border:none','border-radius:8px',
          'padding:10px 16px','font-size:16px','cursor:pointer'
        ].join(';');
        btn.addEventListener('click', () => { try { window.close(); } catch (_) {} });

        wrap.appendChild(h1);
        wrap.appendChild(p);
        wrap.appendChild(btn);
        try { document.body.appendChild(wrap); } catch (_) {}

        // Swallow user interactions beneath
        try { document.addEventListener('keydown', (e) => e.preventDefault(), { capture: true }); } catch (_) {}
        try { document.addEventListener('click', (e) => e.preventDefault(), { capture: true }); } catch (_) {}
      }, 250);
    }, 150);
  }

  // Messaging handlers
  function onMessage(e) {
    const data = (e && e.data) || e;
    if (!data || typeof data !== 'object') return;
    if (data.type === 'takeover-request') {
      // If I am leader and the request is not from me, prepare to release
      if (iAmLeader && data.from !== tabId) {
        releaseOnRequest = true;
      }
    } else if (data.type === 'lease-released') {
      // Followers can try acquire quicker
      if (!iAmLeader) setTimeout(() => safeTryAcquire(), 50);
    } else if (data.type === 'please-close') {
      // Best-effort: attempt to close this tab (works in some contexts/PWAs)
      attemptCloseSelf();
    } else if (data.type === 'leadership-confirmed') {
      // The new leader confirmed acquisition; try closing once more just in case
      attemptCloseSelf();
    } else if (data.type === 'leader-announcement') {
      // Not strictly needed; ignore
    }
  }

  // Listen on BroadcastChannel
  if (bc) {
    try { bc.onmessage = onMessage; } catch (_) {}
  }

  // Fallback via storage events
  window.addEventListener('storage', (ev) => {
    try {
      if (ev.key === CMD_KEY && ev.newValue) {
        const payload = JSON.parse(ev.newValue);
        if (payload && payload.msg) onMessage(payload.msg);
      } else if (ev.key === LEASE_KEY) {
        // If leader lost lease externally, downgrade
        const lease = readLease();
        if (iAmLeader && (!lease || lease.ownerId !== tabId)) becomeFollower();
      }
    } catch (_) {}
  });

  // Initial acquisition attempt
  if (tryAcquire(tabId)) {
    becomeLeader();
  } else {
    iAmLeader = false;
    ui.show();
    scheduleFollowerCheck();
  }

  // Lifecycle hooks
  window.addEventListener('beforeunload', () => {
    // Do not clear lease on unload; rely on TTL to avoid refresh warning flash.
    try { if (iAmLeader) writeLease(tabId); } catch (_) {}
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible' && !iAmLeader) {
      // Try faster reacquire when user focuses this tab
      setTimeout(() => safeTryAcquire(), 100);
    }
  });

  /** Stop the guard and remove UI. */
  function stop() {
    stopped = true;
    clearHeartbeat();
    clearFollowerCheck();
    try { if (bc) bc.close(); } catch (_) {}
    ui.remove();
  }

  const controller = {
    /**
     * Returns whether the current tab holds the lease.
     * @returns {boolean}
     */
    isLeader: () => iAmLeader,
    /**
     * Requests leadership and attempts to acquire it shortly after.
     * @returns {Promise<void>}
     */
    async forceTakeover() {
      sendCmd(bc, { type: 'takeover-request', from: tabId });
      await new Promise(r => setTimeout(r, 250));
      safeTryAcquire();
    },
    /** Stops the guard and removes overlay/UI for this tab. */
    stop
  };

  return attachDebugController(controller);
}

function attachDebugController(controller) {
  try {
    window.tabGuardLeader = controller;
    // Optional app-scoped alias
    window.lytsoftTabGuard = controller;
  } catch (_) {}
  return controller;
}
